import { Button, Fade, Flex, Icon, Text } from '@chakra-ui/react';
import usePanelList from 'pages/PagePanel/hooks/usePanelList';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { PanelSelector } from 'store/selectors';
import { CheckMarkIcon } from 'ui/icons/Icons';
import { getPanelPower } from 'utils/calculation/calculation';
import { capitalize } from 'utils/stringUtils';

type Modes = 'panel' | 'kw';

type Props = {
  totalPanels: number;
  estimatedPanelAmtNeed: number;
};

const CurrentPanelAmountIndicator = ({
  totalPanels,
  estimatedPanelAmtNeed,
}: Props) => {
  const { t } = useTranslation();
  const [currentMode, setCurrentMode] = useState<Modes>('panel');
  const panelList = usePanelList();
  const panelId = useSelector(PanelSelector);
  const panelPower = useMemo(
    () => getPanelPower(panelId, panelList),
    [panelId, panelList]
  );
  const production = (panelPower * totalPanels).toFixed(3);
  const hasReachedGoal = totalPanels >= estimatedPanelAmtNeed;
  const rotation = currentMode === 'kw' ? 'rotateX(180deg)' : '';
  const scale = hasReachedGoal ? 'scale(1.05)' : 'scale(1)';

  return (
    <Flex position="relative" minWidth="75px" height="55px" alignItems="center">
      <Button
        variant="unstyled"
        position="absolute"
        background={hasReachedGoal ? 'secondaryColor' : '#ffffff'}
        color="primaryColor"
        _hover={{}}
        w="auto"
        textAlign="center"
        fontWeight="bold"
        fontSize={{ base: 'xs', md: 'sm' }}
        h="fit-content"
        py="3px !important"
        px="10px !important"
        borderRadius="6px"
        transition="all 0.3s ease-in-out"
        transform={`${rotation} ${scale}`}
        boxShadow={hasReachedGoal ? '0 0 5px 5px rgba(255,215,1, 0.2)' : 'sm'}
        border={
          hasReachedGoal
            ? '1px solid var(--chakra-colors-primaryColor)'
            : '1px solid transparent'
        }
        onClick={() =>
          setCurrentMode((prev) => (prev === 'panel' ? 'kw' : 'panel'))
        }
      >
        {/* //--------------------- MODE PANELS ------------------------ */}

        {currentMode === 'panel' && (
          <Flex justifyContent="center" alignItems="center" pt="1px">
            <Fade
              in
              transition={{
                enter: { duration: 0.2, delay: 0.1 },
                exit: { duration: 0.2 },
              }}
            >
              {!hasReachedGoal ? (
                <>
                  <Text color="secondaryColor" display="inline">
                    {totalPanels}
                  </Text>
                  <Text color="#a2acbf" display="inline">
                    /{estimatedPanelAmtNeed}
                  </Text>
                  <Text color="secondaryColor" display="inline">
                    {` ${capitalize(t('common.panels'))}`}
                  </Text>
                </>
              ) : (
                <>
                  <Icon as={CheckMarkIcon} boxSize="18px" marginRight="5px" />
                  <Text height="100%" color="white" display="inline">
                    {totalPanels} {` ${capitalize(t('common.panels'))}`}
                  </Text>
                </>
              )}
            </Fade>
          </Flex>
        )}

        {/* //--------------------- MODE KW ------------------------ */}
        {currentMode === 'kw' && (
          <Flex
            transform="rotateX(180deg)"
            justifyContent="center"
            alignItems="center"
          >
            <Fade
              in
              transition={{
                enter: { duration: 0.2, delay: 0.1 },
                exit: { duration: 0.2 },
              }}
            >
              <Text color={hasReachedGoal ? 'white' : 'primaryColor'} pt="2px">
                {hasReachedGoal && (
                  <Icon as={CheckMarkIcon} boxSize="18px" marginRight="5px" />
                )}
                {`${production} kWh`}
              </Text>
            </Fade>
          </Flex>
        )}
      </Button>
    </Flex>
  );
};

export default CurrentPanelAmountIndicator;
